/**
 * Admin Pricing Page
 * Manage AI model pricing with API Cost + Markup system
 * Cursor-style pricing model
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DollarSign,
  Edit2,
  Save,
  X,
  RefreshCw,
  TrendingUp,
  Zap,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle2,
  Percent,
  Calculator,
  Download,
  Upload,
  Bot,
  Brain,
  Sparkles,
  Cpu,
  Server,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getPricingConfig,
  savePricingConfig,
  onPricingConfigChange,
  FirestorePricing,
  FirestorePricingConfig
} from '@/services/firebase/adminFirestore';

// Types
type Provider = 'anthropic' | 'openai' | 'google' | 'xai' | 'ollama';
type Tier = 'budget' | 'standard' | 'premium' | 'flagship';

interface ModelPricing {
  id: string;
  modelId: string;
  displayName: string;
  provider: Provider;
  tier: Tier;
  contextWindow: number;
  apiInput: number;      // What we pay (per 1M tokens)
  apiOutput: number;     // What we pay (per 1M tokens)
  input: number;         // What we sell (API + markup)
  output: number;        // What we sell (API + markup)
  isReasoning?: boolean;
  description?: string;
  enabled: boolean;
  lastUpdated: Date;
}

// Provider info
const PROVIDER_INFO: Record<Provider, { name: string; color: string; icon: React.ReactNode }> = {
  openai: { name: 'OpenAI', color: 'bg-green-500', icon: <Bot size={16} className="text-green-500" /> },
  anthropic: { name: 'Anthropic', color: 'bg-orange-500', icon: <Brain size={16} className="text-orange-500" /> },
  google: { name: 'Google AI', color: 'bg-blue-500', icon: <Sparkles size={16} className="text-blue-500" /> },
  xai: { name: 'xAI', color: 'bg-purple-500', icon: <Zap size={16} className="text-purple-500" /> },
  ollama: { name: 'Ollama', color: 'bg-gray-500', icon: <Server size={16} className="text-gray-500" /> },
};

const TIER_INFO: Record<Tier, { name: string; color: string }> = {
  budget: { name: 'Budget', color: 'bg-gray-500' },
  standard: { name: 'Standard', color: 'bg-blue-500' },
  premium: { name: 'Premium', color: 'bg-purple-500' },
  flagship: { name: 'Flagship', color: 'bg-yellow-500' },
};

// Default markup
const DEFAULT_MARKUP = 1.20; // 20%

// Default pricing data (API costs from providers)
const DEFAULT_PRICING: Omit<ModelPricing, 'id' | 'input' | 'output' | 'lastUpdated' | 'enabled'>[] = [
  // ANTHROPIC
  { modelId: 'claude-opus-4.5', displayName: 'Claude Opus 4.5', provider: 'anthropic', tier: 'flagship', contextWindow: 200000, apiInput: 5.00, apiOutput: 25.00, description: 'Most intelligent Claude model' },
  { modelId: 'claude-sonnet-4.5', displayName: 'Claude Sonnet 4.5', provider: 'anthropic', tier: 'premium', contextWindow: 200000, apiInput: 3.00, apiOutput: 15.00, description: 'Best balance of intelligence and speed' },
  { modelId: 'claude-3.5-sonnet', displayName: 'Claude 3.5 Sonnet', provider: 'anthropic', tier: 'standard', contextWindow: 200000, apiInput: 3.00, apiOutput: 15.00 },
  { modelId: 'claude-3.5-haiku', displayName: 'Claude 3.5 Haiku', provider: 'anthropic', tier: 'budget', contextWindow: 200000, apiInput: 0.80, apiOutput: 4.00, description: 'Fast and cost-effective' },

  // OPENAI
  { modelId: 'gpt-4.5-turbo', displayName: 'GPT-4.5 Turbo', provider: 'openai', tier: 'flagship', contextWindow: 128000, apiInput: 3.00, apiOutput: 15.00, description: 'Latest GPT-4.5 model' },
  { modelId: 'gpt-4o', displayName: 'GPT-4o', provider: 'openai', tier: 'premium', contextWindow: 128000, apiInput: 2.50, apiOutput: 10.00, description: 'Omni model with vision' },
  { modelId: 'gpt-4o-mini', displayName: 'GPT-4o Mini', provider: 'openai', tier: 'budget', contextWindow: 128000, apiInput: 0.15, apiOutput: 0.60, description: 'Small and fast' },
  { modelId: 'gpt-4-turbo', displayName: 'GPT-4 Turbo', provider: 'openai', tier: 'premium', contextWindow: 128000, apiInput: 10.00, apiOutput: 30.00 },
  { modelId: 'o1', displayName: 'O1', provider: 'openai', tier: 'flagship', contextWindow: 200000, apiInput: 15.00, apiOutput: 60.00, isReasoning: true, description: 'Advanced reasoning model' },
  { modelId: 'o1-mini', displayName: 'O1 Mini', provider: 'openai', tier: 'standard', contextWindow: 128000, apiInput: 3.00, apiOutput: 12.00, isReasoning: true },
  { modelId: 'o3-mini', displayName: 'O3 Mini', provider: 'openai', tier: 'standard', contextWindow: 200000, apiInput: 1.10, apiOutput: 4.40, isReasoning: true, description: 'Efficient reasoning' },

  // GOOGLE
  { modelId: 'gemini-2.0-flash', displayName: 'Gemini 2.0 Flash', provider: 'google', tier: 'standard', contextWindow: 1000000, apiInput: 0.10, apiOutput: 0.40, description: 'Latest Gemini model' },
  { modelId: 'gemini-1.5-pro', displayName: 'Gemini 1.5 Pro', provider: 'google', tier: 'premium', contextWindow: 2000000, apiInput: 1.25, apiOutput: 5.00 },
  { modelId: 'gemini-1.5-flash', displayName: 'Gemini 1.5 Flash', provider: 'google', tier: 'budget', contextWindow: 1000000, apiInput: 0.075, apiOutput: 0.30 },

  // XAI
  { modelId: 'grok-2', displayName: 'Grok 2', provider: 'xai', tier: 'premium', contextWindow: 131072, apiInput: 2.00, apiOutput: 10.00, description: 'Latest Grok model' },
  { modelId: 'grok-beta', displayName: 'Grok Beta', provider: 'xai', tier: 'standard', contextWindow: 131072, apiInput: 5.00, apiOutput: 15.00 },
];

// Helper function to convert ModelPricing to FirestorePricing
const toFirestorePricing = (model: ModelPricing): FirestorePricing => ({
  id: model.id,
  modelId: model.modelId,
  displayName: model.displayName,
  provider: model.provider,
  tier: model.tier,
  contextWindow: model.contextWindow,
  apiInput: model.apiInput,
  apiOutput: model.apiOutput,
  input: model.input,
  output: model.output,
  isReasoning: model.isReasoning,
  description: model.description,
  enabled: model.enabled,
  lastUpdated: model.lastUpdated,
});

// Helper function to convert FirestorePricing to ModelPricing
const fromFirestorePricing = (fp: FirestorePricing): ModelPricing => ({
  id: fp.id,
  modelId: fp.modelId,
  displayName: fp.displayName,
  provider: fp.provider as Provider,
  tier: fp.tier as Tier,
  contextWindow: fp.contextWindow,
  apiInput: fp.apiInput,
  apiOutput: fp.apiOutput,
  input: fp.input,
  output: fp.output,
  isReasoning: fp.isReasoning,
  description: fp.description,
  enabled: fp.enabled,
  lastUpdated: fp.lastUpdated instanceof Date ? fp.lastUpdated : new Date(fp.lastUpdated),
});

export function AdminPricing() {
  const [pricing, setPricing] = useState<ModelPricing[]>([]);
  const [markup, setMarkup] = useState(DEFAULT_MARKUP);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ModelPricing>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProvider, setFilterProvider] = useState<Provider | 'all'>('all');
  const [filterTier, setFilterTier] = useState<Tier | 'all'>('all');
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Initialize default pricing if Firebase returns null
  const initializeDefaultPricing = useCallback(async () => {
    const initialPricing: ModelPricing[] = DEFAULT_PRICING.map(model => ({
      ...model,
      id: crypto.randomUUID(),
      input: model.apiInput * DEFAULT_MARKUP,
      output: model.apiOutput * DEFAULT_MARKUP,
      enabled: true,
      lastUpdated: new Date(),
    }));
    setPricing(initialPricing);
    setMarkup(DEFAULT_MARKUP);

    // Save initial data to Firebase
    try {
      await savePricingConfig({
        markup: DEFAULT_MARKUP,
        pricing: initialPricing.map(toFirestorePricing),
      });
    } catch (error) {
      console.error('Failed to save initial pricing to Firebase:', error);
    }
  }, []);

  // Load pricing from Firebase
  const loadPricing = useCallback(async () => {
    setIsLoading(true);
    try {
      const config = await getPricingConfig();
      if (config) {
        setPricing(config.pricing.map(fromFirestorePricing));
        setMarkup(config.markup || DEFAULT_MARKUP);
      } else {
        // Initialize with default pricing if no data in Firebase
        await initializeDefaultPricing();
      }
    } catch (error) {
      console.error('Failed to load pricing from Firebase:', error);
      setSaveMessage({ type: 'error', text: 'Failed to load pricing data' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
    setIsLoading(false);
  }, [initializeDefaultPricing]);

  // Real-time listener for pricing changes
  useEffect(() => {
    const unsubscribe = onPricingConfigChange((config: FirestorePricingConfig | null) => {
      if (config) {
        setPricing(config.pricing.map(fromFirestorePricing));
        setMarkup(config.markup || DEFAULT_MARKUP);
        setIsLoading(false);
      } else if (!isLoading) {
        // Only initialize if we're not already loading
        initializeDefaultPricing();
      }
    });

    return () => unsubscribe();
  }, [initializeDefaultPricing, isLoading]);

  // Initial load
  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  // Save pricing to Firebase
  const savePricingToFirebase = async (data: ModelPricing[], markupValue: number) => {
    setIsSaving(true);
    try {
      await savePricingConfig({
        markup: markupValue,
        pricing: data.map(toFirestorePricing),
      });
    } catch (error) {
      console.error('Failed to save pricing to Firebase:', error);
      setSaveMessage({ type: 'error', text: 'Failed to save pricing data' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
    setIsSaving(false);
  };

  // Recalculate all prices with new markup
  const updateMarkup = async (newMarkup: number) => {
    const updatedPricing = pricing.map(model => ({
      ...model,
      input: model.apiInput * newMarkup,
      output: model.apiOutput * newMarkup,
      lastUpdated: new Date(),
    }));
    setMarkup(newMarkup);
    setPricing(updatedPricing);
    await savePricingToFirebase(updatedPricing, newMarkup);
    setSaveMessage({ type: 'success', text: 'Markup updated successfully!' });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Toggle model enabled
  const toggleModel = async (id: string) => {
    const updatedPricing = pricing.map(model =>
      model.id === id ? { ...model, enabled: !model.enabled, lastUpdated: new Date() } : model
    );
    setPricing(updatedPricing);
    await savePricingToFirebase(updatedPricing, markup);
  };

  // Start editing
  const startEdit = (model: ModelPricing) => {
    setEditingId(model.id);
    setEditForm({ ...model });
  };

  // Save edit
  const saveEdit = async () => {
    if (!editingId || !editForm) return;

    const updatedPricing = pricing.map(model =>
      model.id === editingId
        ? {
            ...model,
            ...editForm,
            input: (editForm.apiInput || model.apiInput) * markup,
            output: (editForm.apiOutput || model.apiOutput) * markup,
            lastUpdated: new Date(),
          }
        : model
    );
    setPricing(updatedPricing);
    await savePricingToFirebase(updatedPricing, markup);
    setEditingId(null);
    setEditForm({});
    setSaveMessage({ type: 'success', text: 'Model pricing updated!' });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Filter models
  const filteredPricing = useMemo(() => {
    return pricing.filter(model => {
      const matchesSearch = model.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           model.modelId.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesProvider = filterProvider === 'all' || model.provider === filterProvider;
      const matchesTier = filterTier === 'all' || model.tier === filterTier;
      return matchesSearch && matchesProvider && matchesTier;
    });
  }, [pricing, searchQuery, filterProvider, filterTier]);

  // Calculate stats
  const stats = useMemo(() => {
    const enabledModels = pricing.filter(m => m.enabled);
    const avgMarkupPercent = ((markup - 1) * 100).toFixed(0);
    const totalModels = pricing.length;
    const providers = new Set(pricing.map(m => m.provider)).size;

    // Calculate average profit margin
    const totalApiCost = enabledModels.reduce((sum, m) => sum + m.apiInput + m.apiOutput, 0);
    const totalSellPrice = enabledModels.reduce((sum, m) => sum + m.input + m.output, 0);

    return {
      totalModels,
      enabledModels: enabledModels.length,
      providers,
      markupPercent: avgMarkupPercent,
      avgProfit: totalApiCost > 0 ? ((totalSellPrice - totalApiCost) / totalApiCost * 100).toFixed(1) : '0',
    };
  }, [pricing, markup]);

  // Export pricing
  const exportPricing = () => {
    const data = JSON.stringify({ pricing, markup }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ochtopus-pricing.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Format price
  const formatPrice = (price: number) => {
    return price < 0.01 ? `$${price.toFixed(4)}` : `$${price.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
        <p className="text-text-secondary">Loading pricing data from Firebase...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Model Pricing</h1>
          <p className="text-text-secondary">Manage AI model prices (API Cost + {stats.markupPercent}% markup)</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportPricing} className="btn btn-secondary">
            <Download size={16} />
            Export
          </button>
          <button onClick={loadPricing} className="btn btn-secondary">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Save Message */}
      {saveMessage && (
        <div className={cn(
          'flex items-center gap-2 p-4 rounded-lg',
          saveMessage.type === 'success' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
        )}>
          {saveMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
          {saveMessage.text}
        </div>
      )}

      {/* Stats & Markup */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Calculator className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{stats.totalModels}</p>
              <p className="text-sm text-text-secondary">Total Models</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{stats.enabledModels}</p>
              <p className="text-sm text-text-secondary">Enabled Models</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Zap className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-primary">{stats.providers}</p>
              <p className="text-sm text-text-secondary">AI Providers</p>
            </div>
          </div>
        </div>

        {/* Markup Control */}
        <div className="bg-bg-card border border-accent/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-yellow-500/10">
              <Percent className="w-5 h-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-text-primary">{stats.markupPercent}%</p>
              <p className="text-sm text-text-secondary">Profit Markup</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="1"
              max="2"
              step="0.01"
              value={markup}
              onChange={(e) => setMarkup(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-bg-elevated rounded-lg appearance-none cursor-pointer accent-accent"
            />
            <button
              onClick={() => updateMarkup(markup)}
              disabled={isSaving}
              className="btn btn-primary btn-sm disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
            </button>
          </div>
          <p className="text-xs text-text-tertiary mt-2">
            {((markup - 1) * 100).toFixed(0)}% markup = ~{stats.avgProfit}% avg profit
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search models..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <select
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value as Provider | 'all')}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          <option value="all">All Providers</option>
          {Object.entries(PROVIDER_INFO).map(([key, value]) => (
            <option key={key} value={key}>{value.name}</option>
          ))}
        </select>
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value as Tier | 'all')}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          <option value="all">All Tiers</option>
          {Object.entries(TIER_INFO).map(([key, value]) => (
            <option key={key} value={key}>{value.name}</option>
          ))}
        </select>
      </div>

      {/* Pricing Table */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-bg-elevated">
                <th className="text-left px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Model</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Provider</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Tier</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary uppercase">API Input</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary uppercase">API Output</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Sell Input</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Sell Output</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-text-tertiary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {filteredPricing.map((model) => {
                const providerInfo = PROVIDER_INFO[model.provider];
                const tierInfo = TIER_INFO[model.tier];
                const isEditing = editingId === model.id;

                return (
                  <tr key={model.id} className={cn(
                    'hover:bg-bg-elevated transition-colors',
                    !model.enabled && 'opacity-50'
                  )}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-text-primary">{model.displayName}</p>
                        <p className="text-xs text-text-tertiary font-mono">{model.modelId}</p>
                        {model.isReasoning && (
                          <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-xs rounded bg-yellow-500/10 text-yellow-500">
                            <Zap size={10} />
                            Reasoning
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span>{providerInfo.icon}</span>
                        <span className="text-sm text-text-secondary">{providerInfo.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(
                        'px-2 py-1 text-xs rounded-full',
                        tierInfo.color + '/20',
                        'text-' + tierInfo.color.replace('bg-', '')
                      )}>
                        {tierInfo.name}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.apiInput || 0}
                          onChange={(e) => setEditForm(prev => ({ ...prev, apiInput: parseFloat(e.target.value) }))}
                          className="w-24 px-2 py-1 text-sm text-right rounded bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                        />
                      ) : (
                        <span className="text-sm text-text-tertiary">{formatPrice(model.apiInput)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editForm.apiOutput || 0}
                          onChange={(e) => setEditForm(prev => ({ ...prev, apiOutput: parseFloat(e.target.value) }))}
                          className="w-24 px-2 py-1 text-sm text-right rounded bg-bg-base border border-border-default focus:border-accent focus:outline-none"
                        />
                      ) : (
                        <span className="text-sm text-text-tertiary">{formatPrice(model.apiOutput)}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-green-500">{formatPrice(model.input)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-green-500">{formatPrice(model.output)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={model.enabled}
                          onChange={() => toggleModel(model.id)}
                          className="sr-only peer"
                        />
                        <div className={cn(
                          "w-9 h-5 rounded-full peer transition-colors",
                          "peer-checked:after:translate-x-full peer-checked:after:border-white",
                          "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                          "after:bg-white after:border-gray-300 after:border after:rounded-full",
                          "after:h-4 after:w-4 after:transition-all",
                          model.enabled ? 'bg-green-500' : 'bg-bg-elevated'
                        )} />
                      </label>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {isEditing ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={saveEdit}
                            className="p-1.5 rounded hover:bg-green-500/10 text-green-500 transition-colors"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-500 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(model)}
                          className="p-1.5 rounded hover:bg-bg-elevated text-text-tertiary transition-colors"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredPricing.length === 0 && (
          <div className="text-center py-12 text-text-tertiary">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No models found</p>
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-accent/10 border border-accent/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-accent">Pricing Model</h4>
            <p className="text-sm text-text-secondary mt-1">
              Following Cursor's pricing model: API costs are sourced from each provider and a {stats.markupPercent}% markup is applied.
              Prices shown are per 1 million tokens. Input tokens are typically 3-5x cheaper than output tokens.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
